import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The login request is the one 401 that must NOT trigger auto-logout.
const LOGIN_PATH = '/api/auth/login';

// Auto-logout on 401.
//
// EXCEPT on the login request itself. A 401 from /api/auth/login means the
// password was wrong, not that a session expired. Firing the auto-logout there
// set window.location.href, which reloaded the page and destroyed the
// "Invalid email or password" toast in Login.jsx before anyone could read it —
// a mistyped password looked like the page had simply refreshed for no reason.
// Every OTHER 401 still logs out exactly as before.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const isLoginRequest = (err.config?.url || '').includes(LOGIN_PATH);
    if (err.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Auth ────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get('/api/auth/me'),
  changePassword: (data) => api.post('/api/auth/change-password', data),
  // Self-service profile (current user only)
  getProfile: () => api.get('/api/auth/me/profile'),
  updateProfile: (data) => api.patch('/api/auth/me/profile', data),
  changeMyPassword: (data) => api.post('/api/auth/me/change-password', data),
};

// ── Departments ─────────────────────────────────────────────
export const departmentsAPI = {
  list: () => api.get('/api/departments'),
  create: (data) => api.post('/api/departments', data),
  update: (id, data) => api.put(`/api/departments/${id}`, data),
  delete: (id) => api.delete(`/api/departments/${id}`),
};

// ── Projects ────────────────────────────────────────────────
export const projectsAPI = {
  list: (departmentId) => api.get('/api/projects', { params: { department_id: departmentId } }),
  get: (id) => api.get(`/api/projects/${id}`),
  create: (data) => api.post('/api/projects', data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  // Soft-delete: archives the project (all data preserved). No hard-delete is
  // exposed from the UI by design.
  archive: (id) => api.delete(`/api/projects/${id}`),
  listArchived: (departmentId) =>
    api.get('/api/projects/archived', { params: { department_id: departmentId } }),
  restore: (id) => api.post(`/api/projects/${id}/restore`),
  // Hard delete — backend rejects unless the project is archived.
  permanentDelete: (id) => api.delete(`/api/projects/${id}/permanent`),
};

// ── Documents ───────────────────────────────────────────────
export const documentsAPI = {
  list: (projectId) => api.get('/api/documents', { params: { project_id: projectId } }),
  upload: (projectId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/documents/upload?project_id=${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined,
    });
  },
  reindex: (id) => api.post(`/api/documents/${id}/reindex`),
  delete: (id) => api.delete(`/api/documents/${id}`),
  download: (id) => `${API_URL}/api/documents/${id}/download`,
  downloadBlob: (id) => api.get(`/api/documents/${id}/download`, { responseType: 'blob' }),
  viewBlob: (id) => api.get(`/api/documents/${id}/view`, { responseType: 'blob' }),
};

// ── Chat ────────────────────────────────────────────────────
export const chatAPI = {
  getMessages: (projectId) => api.get(`/api/chat/${projectId}/messages`),
  sendMessage: (projectId, message) => api.post(`/api/chat/${projectId}/message`, { message }),
  clearHistory: (projectId) => api.delete(`/api/chat/${projectId}/messages`),
};

// ── Email ───────────────────────────────────────────────────
export const emailAPI = {
  listConfigs: (departmentId) => api.get('/api/email/configs', { params: { department_id: departmentId } }),
  createConfig: (data) => api.post('/api/email/configs', data),
  updateConfig: (id, data) => api.put(`/api/email/configs/${id}`, data),
  deleteConfig: (id) => api.delete(`/api/email/configs/${id}`),
  getGmailAuthUrl: (configId) => api.get(`/api/email/gmail/connect/${configId}`),
  getOutlookAuthUrl: (configId) => api.get(`/api/email/outlook/connect/${configId}`),
  testImapConnection: (configId) => api.post(`/api/email/imap/test/${configId}`),
  syncEmails: (configId) => api.post(`/api/email/sync/${configId}`),
};

// ── CV Screening ────────────────────────────────────────────
export const cvAPI = {
  // legacy single-criteria (kept for backward compatibility)
  getCriteria: (departmentId) => api.get(`/api/cv/criteria/${departmentId}`),
  updateCriteria: (departmentId, data) => api.put(`/api/cv/criteria/${departmentId}`, data),
  // candidates
  getCandidates: ({ projectId, departmentId } = {}) =>
    api.get('/api/cv/candidates', { params: { project_id: projectId, department_id: departmentId } }),
  // There is deliberately NO client method for POST /api/cv/screen/{id}, the
  // sending rescreen. That endpoint re-runs the whole pipeline: it re-matches
  // the position, rewrites status, and emails the candidate whatever the new
  // score decides — potentially contradicting the email they already received.
  // It is still live on the server (retiring it is a separate decision), but an
  // unused export pointing at it is how a button finds its way back onto a row
  // by accident, so the frontend does not carry a handle to it at all.
  //
  // Recompute the screening and nothing else — no email, no status change, no
  // token, no workflow state. Returns old -> new for every column it wrote,
  // plus the recomputed position as a SUGGESTION it deliberately did not write.
  refreshCandidate: (candidateId) => api.post(`/api/cv/refresh/${candidateId}`),
  // Correct a candidate by hand: email, matched position, or both. One PATCH
  // for both fields so the two inline edits share an endpoint and an access
  // check rather than growing a second mechanism.
  //
  // Send only the keys being changed — the endpoint keys off which fields were
  // present, so { position_id: null } clears the position while omitting it
  // leaves it alone.
  updateCandidate: (candidateId, patch) =>
    api.patch(`/api/cv/candidates/${candidateId}`, patch),
  updateCandidateEmail: (candidateId, email) =>
    api.patch(`/api/cv/candidates/${candidateId}`, { email }),
  // ── Person-identity links ────────────────────────────────────────────
  // The chip on the row comes from the candidate list payload (linked_count);
  // the group itself is fetched only when the panel opens, so the list render
  // stays one query.
  getCandidateLinks: (candidateId) =>
    api.get(`/api/cv/candidates/${candidateId}/links`),
  confirmLink: (linkId) => api.post(`/api/cv/links/${linkId}/confirm`),
  unlinkLink: (linkId, reason) =>
    api.post(`/api/cv/links/${linkId}/unlink`, { reason: reason || null }),
  rejectLink: (linkId, reason) =>
    api.post(`/api/cv/links/${linkId}/reject`, { reason: reason || null }),
  // Fetch the CV PDF as a blob (auth header is included automatically)
  getCv: (candidateId) =>
    api.get(`/api/cv/candidates/${candidateId}/cv`, { responseType: 'blob' }),
  // ── Non-response follow-ups ──────────────────────────────────────────
  // Candidates sent a form invite or a schedule link who have not responded
  // for 24h and are not snoozed. Fully derived server-side — there is no
  // stored "needs follow-up" flag and the client must not build one, because
  // the answer changes when a candidate submits, books, gets a position or has
  // their address corrected, none of which this screen is told about.
  //
  // department_id is passed so the tab shows the department it is open on. The
  // server scopes rows independently via allowed_department_ids; this narrows
  // within what the caller may already see and is not the access check.
  listFollowups: (departmentId) =>
    api.get('/api/cv/followups', { params: { department_id: departmentId } }),
  // outcome is one of 'called_will_respond' | 'no_answer' | 'closed'.
  // There is deliberately NO contacted_by in the body: the server takes the
  // caller from the request's bound actor, and a client-supplied field would
  // let anyone attribute a phone call to a colleague.
  recordFollowup: (candidateId, { outcome, note }) =>
    api.post(`/api/cv/followups/${candidateId}`, { outcome, note }),
  // positions (multi)
  listPositions: (departmentId, activeOnly = false) =>
    api.get('/api/cv/positions', { params: { department_id: departmentId, active_only: activeOnly } }),
  createPosition: (data) => api.post('/api/cv/positions', data),
  updatePosition: (id, data) => api.put(`/api/cv/positions/${id}`, data),
  deletePosition: (id) => api.delete(`/api/cv/positions/${id}`),
  // email templates (interview / details / rejection)
  listEmailTemplates: (departmentId) => api.get(`/api/cv/email-templates/${departmentId}`),
  updateEmailTemplate: (templateId, data) => api.put(`/api/cv/email-templates/${templateId}`, data),
};

// ── On-site English assessment ──────────────────────────────
// HR-SIDE ONLY. The two public endpoints (/api/assessments/public/…) are
// deliberately absent: they are called by frontend/public/assessment.html,
// which is a standalone static page served straight off disk by nginx and
// never loads this bundle. Adding them here would invite someone to render
// the assessment inside the React app, which would put AuthProvider, the HR
// bearer in localStorage, and the 401 interceptor into the tab a candidate is
// holding — the exact thing the static page exists to prevent.
export const assessmentsAPI = {
  sendInvite: (candidateId, data) => api.post(`/api/assessments/candidates/${candidateId}/invite`, data),
  listBanks: () => api.get('/api/assessments/banks'),
  mintToken: (candidateId) =>
    api.post(`/api/assessments/candidates/${candidateId}/token`),
};

// ── CV Screening intake (auto-screening toggle + manual screen-now) ──
export const cvScreeningAPI = {
  getSettings: (deptId) => api.get(`/api/cv-screening/settings/${deptId}`),
  updateSettings: (deptId, data) => api.put(`/api/cv-screening/settings/${deptId}`, data),
  listUnscreened: (deptId) => api.get(`/api/cv-screening/unscreened/${deptId}`),
  screenNow: (data) => api.post('/api/cv-screening/screen-now', data),
  extractEmail: (projectId, candidateName) =>
    api.get('/api/cv-screening/extract-email', {
      params: { project_id: projectId, candidate_name: candidateName },
    }),
  sendManualInterviewEmail: (data) =>
    api.post('/api/cv-screening/send-manual-interview-email', data),
  // HR Review Before Send — score without emailing, then send per-candidate
  screenPreview: (data) =>
    api.post('/api/cv-screening/screen-preview', data),
  sendScreenedEmail: (candidateId, tier, email) =>
    api.post('/api/cv-screening/send-screened-email', { candidate_id: candidateId, tier, email }),
  sendScreenedEmailsBatch: (items) =>
    api.post('/api/cv-screening/send-screened-emails-batch', { items }),
};

// ── Forms ───────────────────────────────────────────────────
export const formsAPI = {
  getQuestions: (departmentId) => api.get(`/api/forms/questions/${departmentId}`),
  createQuestion: (departmentId, data) => api.post(`/api/forms/questions/${departmentId}`, data),
  updateQuestion: (id, data) => api.put(`/api/forms/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/api/forms/questions/${id}`),
  getPublicForm: (token) => api.get(`/api/forms/public/${token}`),
  submitForm: (token, data) => api.post(`/api/forms/public/${token}/submit`, data),
  getSubmissions: (projectId) => api.get('/api/forms/submissions', { params: { project_id: projectId } }),
  // Manual form-invite send
  sendForm: (candidateId) => api.post(`/api/forms/send/${candidateId}`),
  // Enriched submissions (includes question texts) — used by HR dashboard
  getSubmissionsEnriched: (departmentId) =>
    api.get('/api/forms/submissions/enriched', { params: { department_id: departmentId } }),
  markReviewed: (submissionId) => api.post(`/api/forms/submissions/${submissionId}/review`),
  // HR override for a held/review candidate — sends the scheduling link now
  screeningOverride: (candidateId) =>
    api.post(`/api/forms/screening/override/${candidateId}`),
  // CSV export — returns a blob
  exportCsv: (departmentId) =>
    api.get('/api/forms/submissions/export', {
      params: { department_id: departmentId },
      responseType: 'blob',
    }),
};

// ── Interviews ──────────────────────────────────────────────
export const interviewsAPI = {
  // Slots (HR)
  getSlots: (departmentId, params = {}) =>
    api.get('/api/interviews/slots', { params: { department_id: departmentId, ...params } }),
  createSlot: (departmentId, data) =>
    api.post('/api/interviews/slots', data, { params: { department_id: departmentId } }),
  createSlotsBulk: (departmentId, data) =>
    api.post('/api/interviews/slots/bulk', data, { params: { department_id: departmentId } }),
  updateSlot: (id, data) => api.put(`/api/interviews/slots/${id}`, data),
  deleteSlot: (id) => api.delete(`/api/interviews/slots/${id}`),
  // Bookings (HR)
  getBookings: (departmentId, params = {}) =>
    api.get('/api/interviews/bookings', { params: { department_id: departmentId, ...params } }),
  cancelBooking: (id, data) => api.put(`/api/interviews/bookings/${id}/cancel`, data),
  rescheduleBooking: (id, data) => api.put(`/api/interviews/bookings/${id}/reschedule`, data),
  markAttendance: (id, data) => api.patch(`/api/interviews/bookings/${id}/attendance`, data),
  sendScheduleLink: (candidateId) => api.post(`/api/interviews/send-schedule/${candidateId}`),
  // Public (candidate)
  getSchedule: (token) => api.get(`/api/interviews/schedule/${token}`),
  bookSlot: (token, data) => api.post(`/api/interviews/schedule/${token}/book`, data),
};

// ── Reports ─────────────────────────────────────────────────
export const reportsAPI = {
  listPositions: (departmentId) => api.get(`/api/reports/${departmentId}/positions`),
  positionSummary: (departmentId, { dateFrom, dateTo, positionId } = {}) =>
    api.get(`/api/reports/${departmentId}/position-summary`, {
      params: { date_from: dateFrom, date_to: dateTo, position_id: positionId || undefined },
    }),
  list: (departmentId) =>
    api.get('/api/reports', { params: { department_id: departmentId } }),
  generate: (data) => api.post('/api/reports/generate', data),
  // Blob fetches — axios attaches the JWT header so auth passes
  downloadBlob: (id) => api.get(`/api/reports/${id}/download`, { responseType: 'blob' }),
  viewBlob: (id) => api.get(`/api/reports/${id}/view`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/api/reports/${id}`),
  getSettings: (departmentId) => api.get(`/api/reports/settings/${departmentId}`),
  updateSettings: (departmentId, data) => api.put(`/api/reports/settings/${departmentId}`, data),
  triggerWeekly: () => api.post('/api/reports/weekly/trigger'),
  sendNow: (departmentId) => api.post(`/api/reports/send-now/${departmentId}`),
  // ── Follow-up Activity: the call log, filtered ────────────────────────
  // A READ-ONLY view over candidate_followups. It is not the Follow-up tab and
  // cannot change it: closing a candidate takes them off that tab for good, and
  // this is how the call they were closed on stays visible afterwards.
  //
  // Every date here filters on contacted_at — when the CALL was made — and
  // never on when the candidate was created. The two are different timelines
  // and the server says so at length.
  //
  // outcome is the STORED spelling: 'called_will_respond' | 'no_answer' |
  // 'closed', or 'all'. Not a shortened 'will_respond' — one value, one name,
  // matching models/candidate_followup.OUTCOMES and the OUTCOME_META the
  // Follow-up tab already renders from. The server rejects anything else with a
  // 400 rather than quietly returning nothing.
  //
  // Deliberately carries NO phone number, unlike cvAPI.listFollowups: that list
  // exists to be dialled from, this one is a report.
  followupActivity: (departmentId, { dateFrom, dateTo, positionId, outcome } = {}) =>
    api.get(`/api/reports/${departmentId}/followup-activity`, {
      params: {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        position_id: positionId || undefined,
        outcome: outcome || undefined,
      },
    }),
  // The SAME report as a PDF. Identical parameters, deliberately — the two
  // endpoints share one fetch server-side, so a filter that changes one
  // changes both, and this signature mirrors followupActivity above so a
  // future parameter cannot be added to one call site and forgotten at the
  // other.
  //
  // responseType blob, which has a consequence the caller must handle: on a
  // 4xx the ERROR body is also a Blob, so error.response.data is not the usual
  // {detail} object and reading it needs .text() first. See downloadFollowupActivityPdf.
  followupActivityPdf: (departmentId, { dateFrom, dateTo, positionId, outcome } = {}) =>
    api.get(`/api/reports/${departmentId}/followup-activity/pdf`, {
      responseType: 'blob',
      params: {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        position_id: positionId || undefined,
        outcome: outcome || undefined,
      },
    }),
};

// Content-Disposition: attachment; filename="followup-activity-hr-2026-09-01-to-2026-09-14.pdf"
//
// THE SERVER NAMES THE FILE and this reads that name rather than rebuilding it
// in JS. The name encodes the department and the date range, so a client-side
// copy would be a second source of truth for what a given export covers —
// and the two would drift the first time either side changed a default.
//
// Requires main.py's CORSMiddleware expose_headers=["Content-Disposition"]:
// the header is not CORS-safelisted, so without that it is readable only
// same-origin and this returns null on a dev server at localhost:3000.
// Returning null rather than a guessed name is deliberate — a fabricated
// filename would hide that the header never arrived.
export function filenameFromDisposition(disposition) {
  if (!disposition) return null;
  // RFC 5987 form first (filename*=UTF-8''...), then the plain quoted form.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) { try { return decodeURIComponent(star[1]); } catch { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
}

// Axios gives a Blob body on an error response when responseType is 'blob',
// so the usual err.response.data.detail is undefined and the message the
// server sent is inside the blob. Without this the UI would show a generic
// failure for a 403 that had a perfectly clear explanation in it.
export async function errorDetailFromBlob(err, fallback) {
  const body = err?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text());
      if (parsed?.detail) return parsed.detail;
    } catch { /* not JSON — fall through to the fallback */ }
  }
  return err?.response?.data?.detail || fallback;
}

// ── Finance Reports ──────────────────────────────────────────
export const financeAPI = {
  upload: (departmentId, reportName, reportType, file) => {
    const formData = new FormData();
    formData.append('department_id', departmentId);
    formData.append('report_name', reportName);
    formData.append('report_type', reportType);
    formData.append('file', file);
    return api.post('/api/finance/reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  list: (departmentId, filters = {}) =>
    api.get('/api/finance/reports', {
      params: {
        department_id: departmentId,
        report_type: filters.reportType || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      },
    }),
  get: (id) => api.get(`/api/finance/reports/${id}`),
  delete: (id) => api.delete(`/api/finance/reports/${id}`),
  reanalyse: (id) => api.post(`/api/finance/reports/${id}/reanalyse`),
  chat: (id, message) => api.post(`/api/finance/reports/${id}/chat`, { message }),
  clearChat: (id) => api.delete(`/api/finance/reports/${id}/chat`),
  downloadPdf: (id) => api.get(`/api/finance/reports/${id}/pdf`, { responseType: 'blob' }),
  compare: (reportIdA, reportIdB) => api.post('/api/finance/reports/compare', { report_id_a: reportIdA, report_id_b: reportIdB }),
};

// ── Call Quality Monitor ────────────────────────────────────
export const callMonitorAPI = {
  getSettings: () => api.get('/api/call-monitor/settings'),
  updateSettings: (data) => api.put('/api/call-monitor/settings', data),
  listCalls: (params = {}) => api.get('/api/call-monitor/calls', { params }),
  getCall: (id) => api.get(`/api/call-monitor/calls/${id}`),
  deleteCall: (id) => api.delete(`/api/call-monitor/calls/${id}`),
  trigger: (data = {}) => api.post('/api/call-monitor/trigger', data),
  refreshCall: (id) => api.post(`/api/call-monitor/calls/${id}/refresh`),
  reanalyse: (id) => api.post(`/api/call-monitor/calls/${id}/reanalyse`),
  weeklySummary: () => api.get('/api/call-monitor/weekly-summary'),
};

// ── WhatsApp ────────────────────────────────────────────────
export const whatsappAPI = {
  getSettings: () => api.get('/api/whatsapp/settings'),
  updateSettings: (data) => api.put('/api/whatsapp/settings', data),
};

// ── Calendar + phone-call reminders ─────────────────────────
export const calendarAPI = {
  getConnection: () => api.get('/api/calendar/connection'),
  updateSettings: (data) => api.put('/api/calendar/settings', data),
  getGoogleAuthUrl: () => api.get('/api/calendar/google/connect'),
  disconnect: () => api.post('/api/calendar/disconnect'),
  sync: () => api.post('/api/calendar/sync'),
  listEvents: () => api.get('/api/calendar/events'),
  updateEventReminder: (eventId, data) => api.put(`/api/calendar/events/${eventId}/reminder`, data),
  testReminder: (eventId) => api.post(`/api/calendar/events/${eventId}/test-reminder`),
  // Uses the next upcoming event automatically — does NOT consume the scheduled reminder slot
  testNextReminder: () => api.post('/api/calendar/test-next-reminder'),
};

// ── Microsoft Teams mentions ────────────────────────────────
export const teamsAPI = {
  getConnection: () => api.get('/api/teams/connection'),
  updateSettings: (data) => api.put('/api/teams/settings', data),
  sync: () => api.post('/api/teams/sync'),
  listMentions: (params = {}) => api.get('/api/teams/mentions', { params }),
  unreadCount: () => api.get('/api/teams/mentions/unread-count'),
  markRead: (id, isRead = true) => api.post(`/api/teams/mentions/${id}/read`, null, { params: { is_read: isRead } }),
  markAllRead: () => api.post('/api/teams/mentions/read-all'),
  deleteMention: (id) => api.delete(`/api/teams/mentions/${id}`),
  updateStatus: (id, status) => api.patch(`/api/teams/mentions/${id}/status`, { status }),
  generateReply: (id) => api.post(`/api/teams/mentions/${id}/generate-reply`),
  saveReply: (id, reply_text) => api.post(`/api/teams/mentions/${id}/send-reply`, { reply_text }),
  listDigests: (limit = 30) => api.get('/api/teams/digests', { params: { limit } }),
  latestDigest: () => api.get('/api/teams/digests/latest'),
  generateDigest: () => api.post('/api/teams/digest/generate'),
};

// ── Admin ───────────────────────────────────────────────────
export const adminAPI = {
  getUsers: () => api.get('/api/admin/users'),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  resetPassword: (id, newPassword) =>
    api.post(`/api/admin/users/${id}/reset-password`, { new_password: newPassword }),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getAuditLogs: (params = {}) => api.get('/api/admin/audit-logs', { params }),
  getUserActivity: (id, days = 7) => api.get(`/api/admin/users/${id}/activity`, { params: { days } }),
};

// ── System Health ───────────────────────────────────────────
export const healthAPI = {
  getStatus: (force = false) =>
    api.get('/api/health/status', { params: force ? { force: true } : {} }),
  getSettings: () => api.get('/api/health/settings'),
  updateSettings: (data) => api.put('/api/health/settings', data),
};

// ── Voice Email (Vapi outbound "Call Me Now") ────────────────
export const vapiOutboundAPI = {
  getSettings: () => api.get('/api/vapi-email/settings'),
  updateSettings: (data) => api.put('/api/vapi-email/settings', data),
  callMd: () => api.post('/api/vapi-email/call-md'),
};
